import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, MapPin } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { fetchCareersPageSettings, fetchPublishedJobs } from '@/services/careerService';
import { careerCopy } from '@/types/career';
import { useLanguage } from '@/context/LanguageContext';

const Careers = () => {
  const { language, tl } = useLanguage();
  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['careers', 'published'],
    queryFn: fetchPublishedJobs,
  });
  const { data: heroSettings } = useQuery({
    queryKey: ['careers', 'page-settings'],
    queryFn: fetchCareersPageSettings,
  });

  const heroBackgroundImage =
    heroSettings?.heroBackgroundImage || jobs[0]?.coverImage || '/placeholder.svg';

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-[#f7f4ef]">
      <Helmet>
        <title>
          {tl('Carrières | Meubles Karim', 'Careers | Meubles Karim', 'Carriere | Meubles Karim')}
        </title>
      </Helmet>

      <section className="relative h-[42vh] min-h-[280px] overflow-hidden md:h-[52vh]">
        <img
          src={heroBackgroundImage}
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 container-custom flex flex-col justify-center px-4">
          <p className="mb-3 text-xs uppercase tracking-[0.35em] text-white/80 md:text-sm">
            {tl(
              heroSettings?.heroEyebrowFr || 'Rejoignez Meubles Karim',
              heroSettings?.heroEyebrowEn || 'Join Meubles Karim',
              heroSettings?.heroEyebrowFr || 'Unisciti a Meubles Karim'
            )}
          </p>
          <h1 className="max-w-4xl text-4xl font-bold leading-tight text-white md:text-6xl">
            {tl(
              heroSettings?.heroTitleFr || 'Carrières',
              heroSettings?.heroTitleEn || 'Careers',
              heroSettings?.heroTitleFr || 'Carriere'
            )}
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-white/90 md:text-lg">
            {tl(
              heroSettings?.heroSubtitleFr ||
                'Découvrez nos offres et évoluez avec une équipe passionnée par le design et le savoir-faire.',
              heroSettings?.heroSubtitleEn ||
                'Explore open roles and grow with a team passionate about design and craftsmanship.',
              heroSettings?.heroSubtitleFr ||
                'Scoprite le nostre posizioni e crescete con un team appassionato di design e artigianato.'
            )}
          </p>
        </div>
      </section>

      <section className="container-custom px-4 py-12 md:py-20">
        <div className="mb-8 md:mb-12">
          <p className="mb-2 text-xs uppercase tracking-[0.3em] text-muted-foreground">
            {tl('Offres', 'Openings', 'Posizioni')}
          </p>
          <h2 className="text-3xl font-normal text-foreground md:text-5xl">
            {tl('Postes ouverts', 'Open positions', 'Posizioni aperte')}
          </h2>
        </div>

        {isLoading ? (
          <div className="py-16 text-center text-muted-foreground">
            {tl('Chargement…', 'Loading…', 'Caricamento…')}
          </div>
        ) : jobs.length === 0 ? (
          <div className="rounded-2xl border border-border/70 bg-white px-6 py-16 text-center text-muted-foreground shadow-soft">
            {tl(
              'Aucune offre publiée pour le moment.',
              'No openings published at the moment.',
              'Nessuna posizione pubblicata al momento.'
            )}
          </div>
        ) : (
          <div className="divide-y divide-border/70 overflow-hidden rounded-2xl border border-border/70 bg-white shadow-soft">
            {jobs.map((job) => (
              <motion.article
                key={job.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.35 }}
              >
                <Link
                  to={`/careers/${job.slug}`}
                  className="group flex flex-col gap-3 px-5 py-5 transition-colors hover:bg-[#faf8f4] sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-6 sm:py-6"
                >
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <h3 className="text-lg leading-snug text-foreground transition-colors group-hover:text-furniture-gold md:text-xl">
                      {careerCopy(job.title, language)}
                    </h3>
                    {careerCopy(job.shortDescription, language)?.trim() ? (
                      <p className="line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                        {careerCopy(job.shortDescription, language)}
                      </p>
                    ) : null}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Briefcase className="h-3.5 w-3.5" />
                        {job.employmentType}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" />
                        {job.location}
                      </span>
                    </div>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-foreground group-hover:text-furniture-gold">
                    {tl('Voir l’offre', 'View role', 'Vedi offerta')}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Careers;
